import { FC, useState, useRef, useEffect } from 'react';
import apiService from '@/services/api.service';

interface SectorOption {
  id: string;
  name: string;
}

interface ProfileEditModalProps {
  user: { name: string; bio: string; avatar: string | null; sector?: { id: string; name: string } | string | null };
  onClose: () => void;
  onSave: (data: { name?: string; bio?: string; avatar?: string; sector_id?: string | null }) => Promise<void>;
}

const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
const MAX_SIZE = 2 * 1024 * 1024; // 2MB
const MIN_DIM = 100;
const CROP_SIZE = 400;

function cropToSquare(file: File): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(img.src);
      if (img.width < MIN_DIM || img.height < MIN_DIM) {
        reject(new Error(`Imagem muito pequena (${img.width}x${img.height}). Minimo: ${MIN_DIM}x${MIN_DIM}px.`));
        return;
      }
      const sSize = Math.min(img.width, img.height);
      const outSize = Math.min(sSize, CROP_SIZE);
      const canvas = document.createElement('canvas');
      canvas.width = outSize;
      canvas.height = outSize;
      const ctx = canvas.getContext('2d')!;
      const sx = (img.width - sSize) / 2;
      const sy = (img.height - sSize) / 2;
      ctx.drawImage(img, sx, sy, sSize, sSize, 0, 0, outSize, outSize);
      canvas.toBlob(
        (blob) => (blob ? resolve(blob) : reject(new Error('Erro ao processar imagem.'))),
        'image/jpeg',
        0.9
      );
    };
    img.onerror = () => reject(new Error('Erro ao carregar imagem.'));
    img.src = URL.createObjectURL(file);
  });
}

const ProfileEditModal: FC<ProfileEditModalProps> = ({ user, onClose, onSave }) => {
  const [name, setName] = useState(user.name);
  const [bio, setBio] = useState(user.bio || '');
  const currentSectorId = user.sector && typeof user.sector === 'object' ? user.sector.id : '';
  const [sectorId, setSectorId] = useState<string>(currentSectorId);
  const [sectors, setSectors] = useState<SectorOption[]>([]);
  const [avatarPreview, setAvatarPreview] = useState<string | null>(user.avatar);
  const [avatarBlob, setAvatarBlob] = useState<Blob | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    apiService.get<SectorOption[]>('/sectors/').then(({ data }) => setSectors(data)).catch(() => {});
  }, []);

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;

    setError(null);

    if (!ALLOWED_TYPES.includes(file.type)) {
      setError('Formato nao suportado. Use JPEG, PNG ou WebP.');
      return;
    }
    if (file.size > MAX_SIZE) {
      setError(`Arquivo muito grande (${(file.size / 1024 / 1024).toFixed(1)}MB). Maximo: 2MB.`);
      return;
    }

    try {
      const blob = await cropToSquare(file);
      setAvatarBlob(blob);
      setAvatarPreview(URL.createObjectURL(blob));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao processar imagem.');
    }
  };

  const handleRemoveAvatar = () => {
    setAvatarPreview(null);
    setAvatarBlob(null);
    setError(null);
  };

  const handleSave = async () => {
    setError(null);
    setIsSaving(true);
    try {
      let avatarUrl = user.avatar;

      // Upload new avatar if selected
      if (avatarBlob) {
        const formData = new FormData();
        formData.append('file', avatarBlob, 'avatar.jpg');
        formData.append('folder', 'avatars');
        const { data } = await apiService.post<{ url: string }>('/upload/image/', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        avatarUrl = data.url;
      } else if (!avatarPreview && user.avatar) {
        // Avatar was removed
        avatarUrl = '';
      }

      const updates: { name?: string; bio?: string; avatar?: string; sector_id?: string | null } = {};
      if (name !== user.name) updates.name = name;
      if (bio !== (user.bio || '')) updates.bio = bio;
      if (avatarUrl !== user.avatar) updates.avatar = avatarUrl || '';
      if (sectorId !== currentSectorId) updates.sector_id = sectorId || null;

      await onSave(updates);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao salvar perfil.');
      setIsSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="absolute inset-0 bg-black/60" />
      <div
        className="relative bg-surface-card border border-theme-border rounded-2xl w-full max-w-md p-6 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-lg font-bold text-content-primary">Editar Perfil</h2>
          <button onClick={onClose} className="text-content-muted hover:text-content-primary transition-colors" aria-label="Fechar">
            <span className="material-symbols-outlined">close</span>
          </button>
        </div>

        {/* Avatar */}
        <div className="flex flex-col items-center mb-6">
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            className="relative group"
            aria-label="Alterar foto"
          >
            {avatarPreview ? (
              <img src={avatarPreview} alt="Preview" className="size-24 rounded-full object-cover border-2 border-theme-border" />
            ) : (
              <div className="size-24 rounded-full bg-accent/10 flex items-center justify-center border-2 border-theme-border">
                <span className="material-symbols-outlined text-4xl text-accent">person</span>
              </div>
            )}
            <div className="absolute inset-0 rounded-full bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
              <span className="material-symbols-outlined text-white">photo_camera</span>
            </div>
          </button>
          <div className="flex items-center gap-2 mt-3">
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="text-xs text-accent font-medium hover:underline"
            >
              Alterar foto
            </button>
            {avatarPreview && (
              <>
                <span className="text-content-muted">|</span>
                <button
                  type="button"
                  onClick={handleRemoveAvatar}
                  className="text-xs text-red-400 font-medium hover:underline"
                >
                  Remover
                </button>
              </>
            )}
          </div>
          <p className="text-xs text-content-muted mt-1">JPEG, PNG ou WebP. Max 2MB, min 100x100px.</p>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            onChange={handleFileSelect}
            className="hidden"
          />
        </div>

        {/* Name */}
        <div className="mb-4">
          <label htmlFor="profile-name" className="block text-sm font-medium text-content-secondary mb-1">Nome</label>
          <input
            id="profile-name"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="w-full bg-surface-primary border border-theme-border rounded-lg px-4 py-2.5 text-content-primary focus:outline-none focus:ring-2 focus:ring-accent/50 focus:border-accent transition-colors"
            maxLength={150}
          />
        </div>

        {/* Bio */}
        <div className="mb-6">
          <label htmlFor="profile-bio" className="block text-sm font-medium text-content-secondary mb-1">Bio</label>
          <textarea
            id="profile-bio"
            value={bio}
            onChange={(e) => setBio(e.target.value)}
            rows={3}
            className="w-full bg-surface-primary border border-theme-border rounded-lg px-4 py-2.5 text-content-primary focus:outline-none focus:ring-2 focus:ring-accent/50 focus:border-accent transition-colors resize-none"
            maxLength={500}
          />
          <p className="text-xs text-content-muted mt-1 text-right">{bio.length}/500</p>
        </div>

        {/* Sector */}
        {sectors.length > 0 && (
          <div className="mb-6">
            <label htmlFor="profile-sector" className="block text-sm font-medium text-content-secondary mb-1">Setor</label>
            <select
              id="profile-sector"
              value={sectorId}
              onChange={(e) => setSectorId(e.target.value)}
              className="w-full bg-surface-primary border border-theme-border rounded-lg px-4 py-2.5 text-content-primary focus:outline-none focus:ring-2 focus:ring-accent/50 focus:border-accent transition-colors"
            >
              <option value="">Sem setor</option>
              {sectors.map((s) => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </select>
          </div>
        )}

        {/* Error */}
        {error && <p className="text-red-400 text-sm mb-4">{error}</p>}

        {/* Actions */}
        <div className="flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-lg border border-theme-border text-content-secondary text-sm font-medium hover:bg-surface-secondary transition-colors"
            disabled={isSaving}
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={isSaving || !name.trim()}
            className="px-6 py-2 rounded-lg bg-accent text-accent-contrast text-sm font-bold hover:bg-accent-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSaving ? 'Salvando...' : 'Salvar'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProfileEditModal;
